// Send money — Web Portal v2 "Money" screen.
//
// Voye does not move money. Ria, a licensed transmitter, does: it owns
// identity verification, the exchange rate, the fee and the payout in Haiti.
// Voye owns the account, the recipients and the history. That split is the
// whole point of the screen, so the design states it plainly rather than
// hiding the handoff — a customer who thinks Voye holds their money will
// bring us a dispute we have no standing to resolve.
//
// GATED OFF. Both this page's flag and the server's VOYE_MONEY_ENABLED must
// be on; with the server flag off the /ria routes are not registered at all,
// so every call here would 404. The route is not mounted when the flag is
// off, so this file ships as dead code until Ria is signed.

import { useQuery, useMutation } from '@tanstack/react-query';
import { Loader2, ArrowUpRight, Banknote } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, EmptyState } from '../../components/shared/primitives';
import { ria, type RiaTransferRef } from '../../lib/api';
import { CUSTOMER_NAV } from '../_navs';
import { useCustomerNav } from '../../lib/pendingAction';

/** What each side of the partnership is responsible for. */
const VOYE_OWNS = [
  'Your account and recipients',
  'History across every product',
  'Receipts and notifications',
];

const RIA_OWNS = [
  'Identity verification',
  'Exchange rate and fees',
  'Payment and payout in Haiti',
];

/** How the recipient can collect. Times are Ria's estimates, not ours. */
const METHODS = [
  { name: 'MonCash',      sub: 'Mobile wallet',            eta: 'Minutes' },
  { name: 'Bank deposit', sub: 'Unibank, Sogebank, BNC',   eta: 'Same day' },
  { name: 'Cash pickup',  sub: 'Ria agent locations',      eta: 'Within the hour' },
];

export function MoneyPage() {
  const transfersQ = useQuery({
    queryKey: ['ria', 'transfers'],
    queryFn: ria.transfers,
    retry: false,
  });

  const open = useMutation({
    mutationFn: ria.openSession,
    onSuccess: () => {
      // The server mints the SSO token and deliberately does NOT return it —
      // handing it to the browser would put a credential for a regulated
      // partner in client memory. The Ria embed picks the session up itself.
      toast.success('Opening a secure Ria session…');
    },
    onError: (e: Error) => toast.error(e.message || 'Could not open a Ria session. Try again shortly.'),
  });

  const transfers = transfersQ.data?.transfers ?? [];

  // Home carries a badge when this role owes an action, so it stays
  // visible from any screen.
  const nav = useCustomerNav(CUSTOMER_NAV);

  return (
    <PortalShell
      nav={nav}
      activeId="dashboard"
      title="Send money"
      subtitle="Transfers run through our licensed partner, Ria. Your history stays here."
    >
      <div className="space-y-5">
        <div className="flex flex-wrap items-stretch gap-5">
          {/* The ownership split. */}
          <section className="relative flex-[2_1_460px] overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 to-brand-700 p-7 text-white">
            <div aria-hidden className="absolute inset-x-0 top-0 flex h-1">
              <span className="w-1/2 bg-[var(--color-interactive)]" />
              <span className="w-1/2 bg-[var(--color-haiti-red)]" />
            </div>

            <p className="text-[11.5px] font-extrabold uppercase tracking-[1.4px] text-[#9FBBF5]">
              Licensed partner · Ria
            </p>
            <h2 className="mt-2.5 max-w-[460px] text-[22px] md:text-[26px] font-extrabold leading-[1.15] tracking-[-0.7px]">
              Ria moves the money. We keep it in one place.
            </h2>

            <div className="mt-5 grid gap-[18px] grid-cols-[repeat(auto-fit,minmax(min(100%,220px),1fr))]">
              <div>
                <p className="text-[12px] font-extrabold uppercase tracking-[0.8px] text-[#9FBBF5]">VoyeLakay</p>
                <ul className="mt-2 space-y-2">
                  {VOYE_OWNS.map((t) => (
                    <li key={t} className="flex gap-2.5 text-[13.5px] text-[#E3ECFD]">
                      <span aria-hidden className="text-[#7FA0F2]">●</span>{t}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[12px] font-extrabold uppercase tracking-[0.8px] text-[#9FBBF5]">Ria</p>
                <ul className="mt-2 space-y-2">
                  {RIA_OWNS.map((t) => (
                    <li key={t} className="flex gap-2.5 text-[13.5px] text-[#E3ECFD]">
                      <span aria-hidden className="text-[#C9B27A]">●</span>{t}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3.5">
              <button
                type="button"
                onClick={() => open.mutate()}
                disabled={open.isPending}
                className="inline-flex items-center gap-1.5 rounded-[14px] bg-white px-5 py-3 text-[14px] font-extrabold text-brand-600 transition-colors hover:bg-brand-50 disabled:opacity-60"
              >
                {open.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                Continue to Ria
                <ArrowUpRight className="h-4 w-4" aria-hidden="true" />
              </button>
              <span className="text-[12.5px] text-[#C9D8FB]">
                Opens a secure Ria session. Rate and fee shown by Ria before you pay.
              </span>
            </div>
          </section>

          {/* How the recipient collects. */}
          <Card padding="p-[22px]" className="flex-[1_1_280px]">
            <h3 className="text-[16px] font-extrabold text-[var(--color-ink)]">How your family can receive</h3>
            <div className="mt-3 flex flex-col">
              {METHODS.map((m, i) => (
                <div key={m.name} className={`flex items-center gap-3 py-3 ${i ? 'border-t border-[var(--color-hairline)]' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-extrabold text-[var(--color-ink)]">{m.name}</p>
                    <p className="truncate text-[12.5px] text-[var(--color-muted)]">{m.sub}</p>
                  </div>
                  <span className="flex-none whitespace-nowrap rounded-[7px] bg-brand-50 px-2.5 py-1 text-[11.5px] font-extrabold text-brand-600">
                    {m.eta}
                  </span>
                </div>
              ))}
            </div>
            <p className="mt-2 text-[11.5px] text-[var(--color-muted)]">Times estimated by Ria.</p>
          </Card>
        </div>

        {/* Recent transfers.
            The server's mirror carries NO amounts — Voye does not hold them,
            by design. The reference and status are all we can honestly show;
            the figure lives with Ria, and inventing one here would be a
            fabricated financial record. */}
        <Card padding="p-0" className="overflow-hidden">
          <h3 className="border-b border-[var(--color-hairline)] px-[22px] py-[18px] text-[16px] font-extrabold text-[var(--color-ink)]">
            Recent transfers
          </h3>

          {transfersQ.isLoading ? (
            <div className="py-10 text-center">
              <Loader2 className="inline h-4 w-4 animate-spin text-[var(--color-muted)]" aria-hidden="true" />
            </div>
          ) : transfers.length === 0 ? (
            <div className="p-6">
              <EmptyState
                icon={Banknote}
                title="No transfers yet"
                body="Once you send through Ria, each transfer's reference and status appear here."
              />
            </div>
          ) : (
            <ul>
              {transfers.map((t: RiaTransferRef, i) => (
                <li key={t.id} className={`flex items-center gap-3.5 px-[22px] py-3.5 ${i ? 'border-t border-[var(--color-hairline)]' : ''}`}>
                  <span className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-[11px] bg-[var(--color-success-bg)] text-[var(--color-success-fg)]">
                    <Banknote className="h-4 w-4" aria-hidden="true" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13.5px] font-extrabold text-[var(--color-ink)]">
                      {t.recipient_display ?? 'Transfer'}
                    </p>
                    <p className="truncate text-[12px] text-[var(--color-muted)]">
                      <span className="font-mono">{t.ria_reference}</span> · {new Date(t.created_at).toLocaleDateString()}
                    </p>
                  </div>
                  <span className="flex-none whitespace-nowrap rounded-[7px] bg-[var(--color-success-bg)] px-2 py-1 text-[11px] font-extrabold uppercase text-[var(--color-success-fg)]">
                    {t.status.replace(/_/g, ' ')}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <p className="border-t border-[var(--color-hairline)] px-[22px] py-3 text-[11.5px] text-[var(--color-muted)]">
            Amounts are held by Ria, not VoyeLakay. Open your Ria session for the full receipt.
          </p>
        </Card>
      </div>
    </PortalShell>
  );
}
